// src/context/AuthContext.tsx
import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { login as loginService, logout as logoutService, getCurrentUser, refreshAccessToken, decodeJwtPayload } from '../services/authService';
import type { User } from '../services/authService';
import { Permission, UserRole } from '../types/user.types';
import { setLogoutHandler } from '../services/api';
import permissoesService from '../services/permissoesService';

// Warn when refresh token has less than this many seconds remaining
const REFRESH_EXPIRY_WARN_SECONDS = 300; // 5 min

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (document: string, password: string, hCaptchaToken?: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasRole: (role: UserRole) => boolean;
  isAdmin: () => boolean;
  /** Operador de secretaria: OPERADOR marcado como de uma secretaria (mesmas funções do OPERADOR). */
  isOperadorSecretaria: boolean;
  sessionExpiring: boolean;
  /** True enquanto a lista de permissões do usuário está sendo buscada no backend. */
  permissionsLoading: boolean;
  /** True se a última tentativa de buscar as permissões no backend falhou. */
  permissionsError: boolean;
  /** Tenta buscar as permissões novamente. */
  retryLoadPermissions: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(sessionStorage.getItem('token')));
  const [sessionExpiring, setSessionExpiring] = useState(false);
  const [userPermissions, setUserPermissions] = useState<Permission[] | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(false);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clears the expiry warning timer
  const clearExpiryTimer = () => {
    if (expiryTimerRef.current !== null) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  };

  // Loads the user's permissions from the API. The backend (role_permission) is the only
  // source of truth — on failure this must NOT guess; it surfaces permissionsError so the
  // UI can block access and offer a retry instead of rendering with a stale/guessed list.
  const loadUserPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    setPermissionsError(false);
    try {
      const { data } = await permissoesService.getMyPermissions();
      if (Array.isArray(data.permissions)) {
        setUserPermissions(data.permissions.map(p => p as Permission));
      } else {
        setPermissionsError(true);
      }
    } catch {
      setPermissionsError(true);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  // Schedules a warning X seconds before the refresh token expires (AC8)
  const scheduleExpiryWarning = (refreshToken: string) => {
    clearExpiryTimer();
    const payload = decodeJwtPayload(refreshToken);
    if (!payload?.exp) return;
    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const msUntilWarn = msUntilExpiry - REFRESH_EXPIRY_WARN_SECONDS * 1000;
    if (msUntilWarn <= 0) {
      setSessionExpiring(true);
      return;
    }
    expiryTimerRef.current = setTimeout(() => setSessionExpiring(true), msUntilWarn);
  };

  // Full logout — clears state, storage, timer and redirects
  const logout = useCallback(() => {
    clearExpiryTimer();
    setSessionExpiring(false);
    logoutService();
    setUser(null);
    setIsAuthenticated(false);
    setUserPermissions(null);
    setPermissionsLoading(false);
    setPermissionsError(false);
  }, []);

  // Register the logout callback in the api interceptor so it can update React state (AC6)
  useEffect(() => {
    setLogoutHandler(logout);
  }, [logout]);

  // On mount: restore session from storage, checking token expiry proactively (AC4, AC7)
  useEffect(() => {
    const init = async () => {
      const token = sessionStorage.getItem('token');
      const storedRefreshToken = sessionStorage.getItem('refreshToken');

      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      const payload = decodeJwtPayload(token);
      const isExpired = payload?.exp ? payload.exp * 1000 < Date.now() : false;

      if (isExpired) {
        // Access token expired — try silent refresh before giving up (AC7)
        const newToken = await refreshAccessToken();
        if (!newToken) {
          logout();
          return;
        }
        // Refresh succeeded — fall through with restored session
      }

      const currentUser = getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
      setIsAuthenticated(true);
      loadUserPermissions();

      // Schedule the expiry warning based on the (possibly rotated) refresh token
      const activeRefreshToken = sessionStorage.getItem('refreshToken') ?? storedRefreshToken;
      if (activeRefreshToken) {
        scheduleExpiryWarning(activeRefreshToken);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-check expiry warning when tab becomes visible again (AC7)
  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.hidden) return;
      const token = sessionStorage.getItem('token');
      if (!token) return;
      const payload = decodeJwtPayload(token);
      const isExpired = payload?.exp ? payload.exp * 1000 < Date.now() : false;
      if (isExpired) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          logout();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [logout]);

  const login = async (document: string, password: string, hCaptchaToken?: string) => {
    try {
      const response = await loginService({ document, password, hCaptchaToken });
      if (response.success && response.token) {
        sessionStorage.setItem('token', response.token);
        sessionStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        setIsAuthenticated(true);
        setSessionExpiring(false);
        loadUserPermissions();
        if (response.refreshToken) {
          scheduleExpiryWarning(response.refreshToken);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const refreshUser = () => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
  };

  const getUserPermissions = useCallback((): Permission[] => {
    // O pacote do Gestor da Parceria (role_permission 'GESTOR_PARCERIA') ja vem somado pelo
    // backend em /auth/me/permissions.
    // The backend (role_permission) is the only source of truth. While the list hasn't
    // loaded yet (or failed to load), fail closed — never guess from a local copy of the
    // role matrix. Callers that need to distinguish "still loading" from "genuinely no
    // permissions" should check permissionsLoading/permissionsError from this context.
    if (userPermissions !== null) {
      return userPermissions;
    }

    return [];
  }, [user, userPermissions]);

  const hasPermission = useCallback((permission: Permission): boolean => {
    return getUserPermissions().includes(permission);
  }, [getUserPermissions]);

  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    if (!permissions || permissions.length === 0) return true;
    const userPerms = getUserPermissions();
    return permissions.some(p => userPerms.includes(p));
  }, [getUserPermissions]);

  const hasRole = useCallback((role: UserRole): boolean => {
    if (!user || !user.role) return false;
    return String(user.role).toUpperCase() === String(role).toUpperCase();
  }, [user]);

  const isAdmin = useCallback((): boolean => {
    if (!user || !user.role) return false;
    return user.role === UserRole.ADMIN;
  }, [user]);

  const isOperadorSecretaria = String(user?.role ?? '').toUpperCase() === 'OPERADOR'
    && Boolean(user?.operadorSecretaria);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      login,
      logout,
      refreshUser,
      hasPermission,
      hasAnyPermission,
      hasRole,
      isAdmin,
      isOperadorSecretaria,
      sessionExpiring,
      permissionsLoading,
      permissionsError,
      retryLoadPermissions: loadUserPermissions,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
